import { useParams } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { AanmeldShell } from "@/components/AanmeldShell";
import { SkeletonLine } from "@/components/ui/skeleton";
import { Speelschema } from "@/features/planning/Speelschema";
import { eigenKorpsIds } from "@/features/planning/eigenKorpsen";
import {
  usePlanning,
  type Planning,
  type PlanningDag,
} from "@/features/planning/usePlanning";
import { useSeizoenen } from "@/features/session/useSeizoenen";
import { datumLang } from "@/i18n/datum";

/** No dag to describe means either the dag is not in the planning or nothing is published yet. */
function uitleg(
  t: TFunction<["planning"]>,
  dag?: PlanningDag,
  planning?: Planning | null,
) {
  if (dag)
    return `${dag.niveauNaam} · ${dag.organiserendKorpsNaam ?? t("planning:dag.geenKorps")}`;

  return t(planning ? "planning:geenDag" : "planning:geenPlanning");
}

/**
 * The dag rides in the path, so a colleague can be sent the dag itself. It reads the same season
 * query the blocks came from rather than fetching one dag: the planning is one document and
 * already cached, so opening a block paints immediately.
 */
export function WedstrijddagPage() {
  const { t } = useTranslation(["planning"]);
  const { dagId } = useParams({ from: "/planning/$dagId" });
  const { data: seizoenen } = useSeizoenen();
  const seizoen = seizoenen?.[0];
  const { data: planning, isPending } = usePlanning(seizoen?.id);

  const dag = planning?.dagen.find((kandidaat) => kandidaat.id === dagId);
  const laadt = !seizoen || isPending;

  return (
    <AanmeldShell
      wide
      terug={{ to: "/planning", label: t("planning:terug") }}
      titel={dag ? datumLang(dag.datum) : t("planning:title")}
      uitleg={
        laadt ? <SkeletonLine className="w-72" /> : uitleg(t, dag, planning)
      }
    >
      {(me) =>
        !dag || !planning ? null : (
          <div className="space-y-6">
            <Speelschema
              seizoenId={planning.seizoenId}
              dag={dag}
              eigen={eigenKorpsIds(me)}
            />
          </div>
        )
      }
    </AanmeldShell>
  );
}
